import * as React from 'react';

import { Button, Row, Toggle, ToggleButtonGroup } from '@rematter/ui-web';
import {
  addDays,
  addMonths,
  addWeeks,
  endOfWeek,
  format,
  startOfWeek,
} from 'date-fns';
import { useAtom } from 'jotai';
import { LucideChevronLeft, LucideChevronRight } from 'lucide-react';

import {
  type CalendarView,
  $calendarDate,
  $calendarView,
} from '../_state/calendar.state';
import { useTranslations } from '../i18n/useTranslations';

const viewOptions: {
  id: CalendarView;
  labelKey: 'month' | 'week' | 'day';
}[] = [
  { id: 'dayGridMonth', labelKey: 'month' },
  { id: 'timeGridWeek', labelKey: 'week' },
  { id: 'timeGridDay', labelKey: 'day' },
];

function navigateDate(
  currentDate: Date,
  view: CalendarView,
  direction: 1 | -1,
): Date {
  switch (view) {
    case 'dayGridMonth':
      return addMonths(currentDate, direction);
    case 'timeGridWeek':
      return addWeeks(currentDate, direction);
    case 'timeGridDay':
      return addDays(currentDate, direction);
  }
}

function formatDateDisplay(date: Date, view: CalendarView): string {
  switch (view) {
    case 'dayGridMonth':
      return format(date, 'MMMM yyyy');
    case 'timeGridWeek': {
      const weekStart = startOfWeek(date);
      const weekEnd = endOfWeek(date);
      if (weekStart.getFullYear() !== weekEnd.getFullYear()) {
        return `${format(weekStart, 'MMM d, yyyy')} - ${format(weekEnd, 'MMM d, yyyy')}`;
      }
      if (weekStart.getMonth() !== weekEnd.getMonth()) {
        return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
      }
      return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'd, yyyy')}`;
    }
    case 'timeGridDay':
      return format(date, 'MMMM d, yyyy');
  }
}

function toDateString(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

function parseDateString(value: string | undefined): Date {
  if (!value) return new Date();
  const parsed = new Date(value + 'T00:00:00');
  return isNaN(parsed.getTime()) ? new Date() : parsed;
}

export const CalendarToolbar = () => {
  const t = useTranslations();
  const [calendarView, setCalendarView] = useAtom($calendarView);
  const [calendarDate, setCalendarDate] = useAtom($calendarDate);

  const currentView = (calendarView || 'dayGridMonth') as CalendarView;
  const currentDate = React.useMemo(
    () => parseDateString(calendarDate),
    [calendarDate],
  );

  const handlePrevious = React.useCallback(() => {
    const newDate = navigateDate(currentDate, currentView, -1);
    setCalendarDate(toDateString(newDate));
  }, [currentDate, currentView, setCalendarDate]);

  const handleNext = React.useCallback(() => {
    const newDate = navigateDate(currentDate, currentView, 1);
    setCalendarDate(toDateString(newDate));
  }, [currentDate, currentView, setCalendarDate]);

  const handleToday = React.useCallback(() => {
    setCalendarDate(toDateString(new Date()));
  }, [setCalendarDate]);

  const dateDisplay = React.useMemo(
    () => formatDateDisplay(currentDate, currentView),
    [currentDate, currentView],
  );

  return (
    <Row yCenter className="@CalendarToolbar justify-between px-4 py-3" gap4>
      <Row yCenter gap2>
        <ToggleButtonGroup
          selectionMode="single"
          selectedKeys={[currentView]}
          onSelectionChange={(v) => {
            const value = Array.from(v.values())[0] as CalendarView;
            if (value) setCalendarView(value);
          }}
        >
          {viewOptions.map((option) => (
            <Toggle key={option.id} id={option.id}>
              {t.toolbar.views[option.labelKey]}
            </Toggle>
          ))}
        </ToggleButtonGroup>
      </Row>

      <Row yCenter gap2>
        <Button
          variant="ghost"
          size="icon"
          icon={<LucideChevronLeft />}
          aria-label={t.toolbar.previous}
          onPress={handlePrevious}
        />
        <Button
          variant="outline"
          size="small"
          className="!h-8 px-3"
          onPress={handleToday}
        >
          {t.toolbar.today}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          icon={<LucideChevronRight />}
          aria-label={t.toolbar.next}
          onPress={handleNext}
        />
        <span className="min-w-[180px] text-center text-sm font-medium">
          {dateDisplay}
        </span>
      </Row>
    </Row>
  );
};
